import { pageTitle } from 'ember-page-title';
import { LinkTo } from '@ember/routing';
import { t } from 'ember-intl';

<template>
  {{pageTitle (t "navigation.home") separator=" | "}}

  {{! Hero Section with RDFa for Person }}
  <section class="hero" aria-labelledby="hero-title">
    <div class="container">
      <div class="hero__content" vocab="https://schema.org/" typeof="Person">
        <meta property="name" content="Boris De Vloed" />
        <meta property="jobTitle" content="Freelance IT Consultant" />
        <link property="url" href="https://devloed.com" />
        <link
          property="sameAs"
          href="https://www.linkedin.com/in/borisdevloed/"
        />

        <h1 id="hero-title" class="hero__title">
          {{t "hero.title"}}
        </h1>
        <p class="hero__subtitle">{{t "hero.subtitle"}}</p>
        <p class="hero__description" property="description">
          {{t "hero.description"}}
        </p>

        <div class="specializations" role="list" aria-label="Specializations">
          <span
            class="specialization-tag"
            role="listitem"
            property="knowsAbout"
          >{{t "specializations.semantic_web"}}</span>
          <span
            class="specialization-tag"
            role="listitem"
            property="knowsAbout"
          >{{t "specializations.linked_data"}}</span>
          <span
            class="specialization-tag"
            role="listitem"
            property="knowsAbout"
          >{{t "specializations.ai_ml"}}</span>
          <span
            class="specialization-tag"
            role="listitem"
            property="knowsAbout"
          >{{t "specializations.data_modeling"}}</span>
          <span
            class="specialization-tag"
            role="listitem"
            property="knowsAbout"
          >{{t "specializations.healthcare_it"}}</span>
        </div>

        <div class="hero__actions">
          <LinkTo @route="contact" class="btn btn--primary">
            {{t "hero.cta_contact"}}
          </LinkTo>
          <LinkTo @route="services" class="btn btn--secondary">
            {{t "hero.cta_services"}}
          </LinkTo>
        </div>
      </div>
    </div>
  </section>

  {{! Brief Services Overview }}
  <section class="section" aria-labelledby="services-preview-title">
    <div class="container">
      <div class="section-header section-header--center">
        <h2 id="services-preview-title" class="section-header__title">{{t
            "home.services_preview.title"
          }}</h2>
        <p class="section-header__description">
          {{t "home.services_preview.description"}}
        </p>
      </div>

      <div class="services-grid">
        <article class="card service-card">
          <svg
            class="service-card__icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"></path>
            <line x1="2" y1="12" x2="22" y2="12"></line>
          </svg>
          <h3 class="card__title service-card__title">{{t
              "home.services_preview.semantic_web.title"
            }}</h3>
          <p class="card__description">
            {{t "home.services_preview.semantic_web.description"}}
          </p>
        </article>

        <article class="card service-card">
          <svg
            class="service-card__icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
            <path d="M2 17l10 5 10-5"></path>
            <path d="M2 12l10 5 10-5"></path>
          </svg>
          <h3 class="card__title service-card__title">{{t
              "home.services_preview.ai_ml.title"
            }}</h3>
          <p class="card__description">
            {{t "home.services_preview.ai_ml.description"}}
          </p>
        </article>

        <article class="card service-card">
          <svg
            class="service-card__icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <path
              d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"
            ></path>
          </svg>
          <h3 class="card__title service-card__title">{{t
              "home.services_preview.data_engineering.title"
            }}</h3>
          <p class="card__description">
            {{t "home.services_preview.data_engineering.description"}}
          </p>
        </article>
      </div>

      <div class="text-center mt-8">
        <LinkTo @route="services" class="btn btn--outline">
          {{t "home.services_preview.cta"}}
        </LinkTo>
      </div>
    </div>
  </section>

  {{! Current Work Highlight }}
  <section class="section section--alt" aria-labelledby="current-work-title">
    <div class="container">
      <div class="section-header">
        <h2 id="current-work-title" class="section-header__title">{{t
            "home.current_focus.title"
          }}</h2>
        <p class="section-header__description">
          {{t "home.current_focus.description"}}
        </p>
      </div>

      <div class="card card--bordered">
        <h3 class="card__title">{{t "home.current_focus.card_title"}}</h3>
        <p class="card__description">
          {{t "home.current_focus.card_description"}}
        </p>
        <div class="mt-4">
          <LinkTo @route="experience" class="btn btn--outline">
            {{t "home.current_focus.cta"}}
          </LinkTo>
        </div>
      </div>
    </div>
  </section>

  {{! Side Project — Open Wine Map }}
  <section class="section" aria-labelledby="side-project-title">
    <div class="container">
      <div class="section-header">
        <h2 id="side-project-title" class="section-header__title">{{t
            "open_wine_map.section_title"
          }}</h2>
        <p class="section-header__description">
          {{t "open_wine_map.section_description"}}
        </p>
      </div>

      <div class="card card--bordered">
        <h3 class="card__title">{{t "open_wine_map.title"}}</h3>
        <p class="card__description">
          {{t "open_wine_map.description"}}
        </p>
        <div class="mt-4">
          <a
            href="https://openwinemap.com"
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn--outline"
            aria-label="Visit Open Wine Map (opens in new window)"
          >
            {{t "open_wine_map.cta"}}
          </a>
        </div>
      </div>
    </div>
  </section>
</template>
